import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, User, CreditCard, FileText, Globe, Clock, Check, Search, CalendarDays } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timezones, setTimezones] = useState([]);
  const [dateFormats, setDateFormats] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [selectedDateFormat, setSelectedDateFormat] = useState('auto');
  const [dateFormatPreview, setDateFormatPreview] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTime, setCurrentTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [showTimezoneDropdown, setShowTimezoneDropdown] = useState(false);

  const fetchUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch user');
      const data = await response.json();
      setUser(data);
      setSelectedTimezone(data.timezone || 'UTC');
      setSelectedDateFormat(data.date_format || 'auto');
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTimezones = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/timezones`);
      if (response.ok) {
        const data = await response.json();
        setTimezones(data.timezones || []);
      }
    } catch (error) {
      console.error('Failed to fetch timezones:', error);
    }
  }, []);

  const fetchDateFormats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/date-formats`);
      if (response.ok) {
        const data = await response.json();
        setDateFormats(data.formats || []);
      }
    } catch (error) {
      console.error('Failed to fetch date formats:', error);
    }
  }, []);

  const fetchDateFormatPreview = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/date-format-preview`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDateFormatPreview(data);
      }
    } catch (error) {
      console.error('Failed to fetch date format preview:', error);
    }
  }, []);

  // Auto-detect browser timezone
  const detectBrowserTimezone = () => {
    try {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTz && timezones.some(tz => tz.value === browserTz)) {
        setSelectedTimezone(browserTz);
        toast.success(`Detected timezone: ${browserTz}`);
      }
    } catch (error) {
      console.error('Could not detect browser timezone:', error);
    }
  };

  // Update current time display
  useEffect(() => {
    const updateTime = () => {
      try {
        const now = new Date();
        const options = {
          timeZone: selectedTimezone || 'UTC',
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        };
        setCurrentTime(now.toLocaleString('en-US', options));
      } catch (error) {
        setCurrentTime('Invalid timezone');
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [selectedTimezone]);

  useEffect(() => {
    fetchUser();
    fetchTimezones();
    fetchDateFormats();
  }, [fetchUser, fetchTimezones, fetchDateFormats]);

  // Fetch date format preview when user is loaded
  useEffect(() => {
    if (user) {
      fetchDateFormatPreview();
    }
  }, [user, fetchDateFormatPreview]);

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/auth/preferences`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          timezone: selectedTimezone,
          date_format: selectedDateFormat
        })
      });

      if (!response.ok) throw new Error('Failed to save preferences');
      
      const updatedUser = await response.json();
      setUser(updatedUser);
      fetchDateFormatPreview(); // Refresh preview
      toast.success('Preferences saved successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Get date preview based on current selection (before saving)
  const getLocalDatePreview = () => {
    const sampleDate = '2026-01-27';
    const [year, month, day] = sampleDate.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = months[parseInt(month, 10) - 1];
    
    let format = selectedDateFormat;
    if (format === 'auto') {
      // Simple region-based detection for preview
      if (selectedTimezone.startsWith('America/') && 
          ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix'].some(tz => selectedTimezone.includes(tz.split('/')[1]))) {
        format = 'MM/DD/YYYY';
      } else if (selectedTimezone.startsWith('Asia/Tokyo') || selectedTimezone.startsWith('Asia/Seoul') || selectedTimezone.startsWith('Asia/Shanghai')) {
        format = 'YYYY-MM-DD';
      } else {
        format = 'DD/MM/YYYY';
      }
    }
    
    switch (format) {
      case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      case 'DD MMM YYYY': return `${day} ${monthShort} ${year}`;
      case 'MMM DD, YYYY': return `${monthShort} ${day}, ${year}`;
      default: return `${day}/${month}/${year}`;
    }
  };

  // Filter timezones based on search
  const filteredTimezones = timezones.filter(tz =>
    tz.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tz.value.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  const hasChanges = selectedTimezone !== (user?.timezone || 'UTC') || 
                     selectedDateFormat !== (user?.date_format || 'auto');

  const planLimits = {
    free: { docs: 10, fileSize: 10 },
    pro: { docs: -1, fileSize: 10 },
    business: { docs: -1, fileSize: 25 }
  };

  const limits = planLimits[user?.subscription_plan] || planLimits.free;
  const hasTimezoneChanged = selectedTimezone !== (user?.timezone || 'UTC');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="rounded-xl"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <h1 className="text-4xl font-bold mb-8">Settings</h1>

        {/* Profile Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-6 h-6 text-violet-600" />
            <h2 className="text-2xl font-bold">Profile</h2>
          </div>
          <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-lg" data-testid="profile-section">
            <div className="flex items-center gap-6 mb-6">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-20 h-20 rounded-full"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white text-3xl font-bold">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h3 className="text-2xl font-bold">{user?.name}</h3>
                <p className="text-slate-600">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Timezone & Date Format Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Globe className="w-6 h-6 text-violet-600" />
            <h2 className="text-2xl font-bold">Timezone & Date Format</h2>
          </div>
          <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-lg" data-testid="timezone-section">
            {/* Current Time Preview */}
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-100">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-violet-600" />
                <span className="text-sm font-medium text-violet-700">Current Time Preview</span>
              </div>
              <p className="text-lg font-semibold text-slate-800" data-testid="current-time-preview">
                {currentTime}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                This is the time in your selected timezone.
              </p>
            </div>

            {/* Timezone Selector */}
            <div className="mb-6">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Your Timezone
              </Label>
              <div className="flex gap-2 mb-2">
                <div className="relative flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setShowTimezoneDropdown(true); }}
                      onFocus={() => setShowTimezoneDropdown(true)}
                      placeholder="Search timezones..."
                      className="pl-10 h-11"
                      data-testid="timezone-search-input"
                    />
                  </div>
                  
                  {/* Timezone Dropdown */}
                  {showTimezoneDropdown && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {filteredTimezones.length === 0 ? (
                        <div className="p-3 text-sm text-slate-500 text-center">No timezones found</div>
                      ) : (
                        filteredTimezones.map((tz) => (
                          <button
                            key={tz.value}
                            onClick={() => {
                              setSelectedTimezone(tz.value);
                              setSearchQuery('');
                              setShowTimezoneDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 hover:bg-violet-50 transition-colors flex items-center justify-between ${
                              selectedTimezone === tz.value ? 'bg-violet-50' : ''
                            }`}
                          >
                            <span className="text-sm">{tz.label}</span>
                            {selectedTimezone === tz.value && (
                              <Check className="w-4 h-4 text-violet-600" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={detectBrowserTimezone}
                  className="h-11 px-4 whitespace-nowrap"
                  data-testid="detect-timezone-btn"
                >
                  Auto-detect
                </Button>
              </div>
              
              {/* Selected timezone display */}
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <span className="text-sm text-slate-500">Selected:</span>
                <span className="ml-2 font-medium text-slate-800" data-testid="selected-timezone">
                  {timezones.find(tz => tz.value === selectedTimezone)?.label || selectedTimezone}
                </span>
              </div>
            </div>

            {/* Date Format Selector */}
            <div className="mb-6 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <CalendarDays className="w-5 h-5 text-violet-600" />
                <Label className="text-sm font-medium text-slate-700">Date Format</Label>
              </div>
              
              <select
                value={selectedDateFormat}
                onChange={(e) => setSelectedDateFormat(e.target.value)}
                className="w-full h-11 rounded-lg border border-slate-200 px-4 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="date-format-select"
              >
                {dateFormats.map((fmt) => (
                  <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
                ))}
              </select>
              
              <p className="text-xs text-slate-500 mt-2">
                This format will be used for date fields in your documents. Auto-detect uses the common format for your timezone region.
              </p>
              
              {/* Date Format Preview */}
              <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
                <div className="flex items-center gap-2 mb-1">
                  <CalendarDays className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-medium text-amber-700">Date Preview</span>
                </div>
                <p className="text-sm text-slate-700">
                  Sample date <code className="px-1.5 py-0.5 rounded bg-white text-violet-600">2026-01-27</code> will appear as: 
                  <strong className="ml-2 text-slate-900">{getLocalDatePreview()}</strong>
                </p>
              </div>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSavePreferences}
              disabled={saving || !hasChanges}
              className={`w-full h-11 rounded-xl font-medium ${
                hasChanges
                  ? 'bg-violet-600 hover:bg-violet-700 text-white'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
              data-testid="save-preferences-btn"
            >
              {saving ? 'Saving...' : hasChanges ? 'Save Preferences' : 'No Changes'}
            </Button>
            
            {hasChanges && (
              <p className="text-xs text-amber-600 text-center mt-2">
                You have unsaved changes
              </p>
            )}
          </div>
        </div>

        {/* Subscription Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <CreditCard className="w-6 h-6 text-violet-600" />
            <h2 className="text-2xl font-bold">Subscription</h2>
          </div>
          <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-lg" data-testid="subscription-section">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold capitalize mb-1">{user?.subscription_plan} Plan</h3>
                <p className="text-slate-600 capitalize">{user?.billing_cycle} billing</p>
              </div>
              <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                user?.subscription_status === 'active'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {user?.subscription_status}
              </span>
            </div>

            {user?.subscription_plan === 'free' && (
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-bold"
                data-testid="upgrade-subscription-btn"
              >
                Upgrade Plan
              </Button>
            )}

            {user?.subscription_plan !== 'free' && (
              <Button
                onClick={() => navigate('/pricing')}
                variant="outline"
                className="w-full h-12 rounded-xl border-2 border-slate-200 hover:border-violet-600"
                data-testid="manage-subscription-btn"
              >
                Manage Subscription
              </Button>
            )}
          </div>
        </div>

        {/* Usage Section */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-6 h-6 text-violet-600" />
            <h2 className="text-2xl font-bold">Usage & Limits</h2>
          </div>
          <div className="p-8 rounded-2xl bg-white border border-slate-200 shadow-lg" data-testid="usage-section">
            <div className="space-y-6">
              {/* Documents Usage */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-700">Documents this month</span>
                  <span className="font-bold">
                    {user?.docs_used_this_month || 0}
                    {limits.docs > 0 ? ` / ${limits.docs}` : ' / Unlimited'}
                  </span>
                </div>
                {limits.docs > 0 && (
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-violet-600 to-indigo-600 transition-all duration-300"
                      style={{
                        width: `${Math.min((user?.docs_used_this_month || 0) / limits.docs * 100, 100)}%`
                      }}
                    ></div>
                  </div>
                )}
              </div>

              {/* File Size Limit */}
              <div className="pt-6 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-700">Max file size</span>
                  <span className="font-bold">{limits.fileSize}MB</span>
                </div>
              </div>

              {/* Features */}
              <div className="pt-6 border-t border-slate-200">
                <h4 className="font-bold mb-3">Available Features</h4>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-slate-700">
                    <div className="w-2 h-2 rounded-full bg-violet-600"></div>
                    Document upload & parsing
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <div className="w-2 h-2 rounded-full bg-violet-600"></div>
                    Template creation
                  </li>
                  {user?.subscription_plan !== 'free' && (
                    <>
                      <li className="flex items-center gap-2 text-slate-700">
                        <div className="w-2 h-2 rounded-full bg-violet-600"></div>
                        Advanced form builder
                      </li>
                      <li className="flex items-center gap-2 text-slate-700">
                        <div className="w-2 h-2 rounded-full bg-violet-600"></div>
                        Conditional logic
                      </li>
                    </>
                  )}
                  {user?.subscription_plan === 'business' && (
                    <>
                      <li className="flex items-center gap-2 text-slate-700">
                        <div className="w-2 h-2 rounded-full bg-violet-600"></div>
                        API access
                      </li>
                      <li className="flex items-center gap-2 text-slate-700">
                        <div className="w-2 h-2 rounded-full bg-violet-600"></div>
                        Custom branding
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Click outside to close dropdown */}
      {showTimezoneDropdown && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowTimezoneDropdown(false)}
        />
      )}
    </div>
  );
};

export default SettingsPage;
